import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { 
  ArrowLeft, Calendar, Users, Clock, Star, ChevronRight,
  X, CheckCircle, User, Mail, Send, Zap, Trophy, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CampaignsPage = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinForm, setJoinForm] = useState({
    email: '',
    roboforex_account: '',
    agreed_terms: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [verificationCode, setVerificationCode] = useState(null);
  const [isResending, setIsResending] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const response = await axios.get(`${API}/campaigns`);
        setCampaigns(response.data.campaigns);
      } catch (error) {
        toast.error('Gagal memuat program');
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  const handleJoinClick = (campaign) => {
    setSelectedCampaign(campaign);
    setShowJoinModal(true);
  };

  const handleJoinSubmit = async (e) => {
    e.preventDefault();
    if (!joinForm.email) {
      toast.error('Mohon masukkan email Anda');
      return;
    }
    if (!joinForm.roboforex_account || joinForm.roboforex_account.length < 5) {
      toast.error('Masukkan nomor akun RoboForex yang valid');
      return;
    }
    if (!joinForm.agreed_terms) {
      toast.error('Anda harus menyetujui syarat dan ketentuan');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/campaigns/${selectedCampaign.id}/join`, {
        email: joinForm.email,
        roboforex_account: joinForm.roboforex_account,
        agreed_terms: joinForm.agreed_terms
      });

      if (response.data.verification_code) {
        setVerificationCode(response.data.verification_code);
      } else {
        toast.success(response.data.message);
        setShowJoinModal(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Gagal bergabung ke program';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!selectedCampaign || !joinForm.email) return;
    setIsResending(true);
    try {
      const response = await axios.post(`${API}/campaigns/resend-code`, {
        email: joinForm.email,
        campaign_id: selectedCampaign.id
      });
      if (response.data.verification_code) {
        setVerificationCode(response.data.verification_code);
        toast.success('Kode verifikasi baru telah dikirim');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Gagal mengirim ulang kode';
      toast.error(message);
    } finally {
      setIsResending(false);
    }
  };

  const filteredCampaigns = campaigns.filter(c => {
    if (filter === 'all') return true;
    if (filter === 'active') return c.status === 'active';
    if (filter === 'upcoming') return c.status === 'upcoming';
    return c.trader_type === filter || c.trader_type === 'all';
  });

  const difficultyColors = {
    'Beginner': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Intermediate': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'Advanced': 'bg-red-500/20 text-red-400 border-red-500/30'
  };

  const statusColors = {
    'active': 'bg-tc-secondary/20 text-tc-secondary',
    'upcoming': 'bg-tc-primary/20 text-tc-primary'
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-tc-bg flex items-center justify-center" data-testid="campaigns-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-tc-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-tc-text-secondary">Memuat program...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-tc-bg" data-testid="campaigns-page">
      {/* Seamless Background Image */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none opacity-20 mix-blend-screen"
        style={{
          backgroundImage: `url('/images/backgrounds/campaigns_page_bg_1774768166294.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      
      {/* Content Overlay */}
      <div className="relative z-10">
        {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-tc-bg/80 backdrop-blur-md border-b border-tc-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-tc-text-secondary hover:text-white transition-colors"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Beranda</span>
          </button>
          <h1 className="text-lg font-heading font-semibold text-white">Program Trading</h1>
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </header>

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-tc-primary/10 border border-tc-primary/20 mb-6">
              <Trophy className="w-4 h-4 text-tc-primary" />
              <span className="text-sm text-tc-primary font-medium">PROGRAM TRADING</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4">
              Bergabung ke program trading
            </h1>
            <p className="text-xl text-tc-text-secondary max-w-2xl mx-auto">
              Belajar bersama komunitas melalui program terstruktur sesuai gaya trading Anda.
            </p>
          </motion.div>

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-wrap gap-3 mb-10 justify-center"
          >
            {[
              { key: 'all', label: 'Semua Program' },
              { key: 'active', label: 'Aktif' },
              { key: 'upcoming', label: 'Segera' },
              { key: 'scalper', label: 'Scalper' },
              { key: 'intraday', label: 'Intraday' },
              { key: 'swing', label: 'Swing' },
              { key: 'position', label: 'Position' }
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  filter === key
                    ? 'bg-tc-primary text-white'
                    : 'bg-tc-surface text-tc-text-secondary hover:bg-tc-surface-highlight'
                }`}
                data-testid={`filter-${key}`}
              >
                {label}
              </button>
            ))}
          </motion.div>

          {/* Campaigns Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign, idx) => (
              <motion.div
                key={campaign.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                className="glass-card rounded-2xl overflow-hidden group"
                data-testid={`campaign-card-${campaign.id}`}
              >
                {/* Image */}
                <div className="relative h-40 overflow-hidden">
                  <img 
                    src={campaign.image} 
                    alt={campaign.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-tc-bg to-transparent" />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${statusColors[campaign.status]}`}>
                      {campaign.status === 'active' ? 'Aktif' : 'Segera'}
                    </span>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full border ${difficultyColors[campaign.difficulty]}`}>
                      {campaign.difficulty === 'Beginner' ? 'Pemula' : campaign.difficulty === 'Intermediate' ? 'Menengah' : campaign.difficulty === 'Advanced' ? 'Lanjutan' : campaign.difficulty}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-lg font-heading font-semibold text-white mb-2 group-hover:text-tc-primary transition-colors">
                    {campaign.title}
                  </h3>
                  <p className="text-sm text-tc-text-secondary mb-4 line-clamp-2">
                    {campaign.description}
                  </p>

                  {/* Meta */}
                  <div className="flex flex-wrap gap-4 text-xs text-tc-text-secondary mb-4">
                    <div className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{campaign.duration}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{formatDate(campaign.start_date)}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="w-3.5 h-3.5" />
                      <span>{campaign.participants}/{campaign.max_participants}</span>
                    </div>
                  </div>

                  {/* Instructor */}
                  <div className="flex items-center gap-2 mb-4 pb-4 border-b border-tc-border">
                    <div className="w-8 h-8 rounded-full bg-tc-primary/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-tc-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-tc-text-secondary">Mentor</p>
                      <p className="text-sm text-white font-medium">{campaign.instructor}</p>
                    </div>
                  </div>

                  {/* CTA */}
                  <Button
                    onClick={() => handleJoinClick(campaign)}
                    className="w-full bg-tc-primary hover:bg-tc-primary-hover text-white font-bold rounded-xl"
                    data-testid={`join-campaign-${campaign.id}`}
                  >
                    GABUNG PROGRAM
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>

          {filteredCampaigns.length === 0 && (
            <div className="text-center py-16">
              <p className="text-tc-text-secondary">Tidak ada program untuk filter ini.</p>
            </div>
          )}
        </div>
      </main>

      {/* Join Campaign Modal */}
      <AnimatePresence>
        {showJoinModal && selectedCampaign && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-tc-bg/90 backdrop-blur-md flex items-center justify-center p-6"
            onClick={(e) => { if (e.target === e.currentTarget) setShowJoinModal(false); }}
            data-testid="join-campaign-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="glass-card rounded-2xl p-8 max-w-lg w-full relative max-h-[90vh] overflow-y-auto"
            >
              {/* Close button */}
              <button
                onClick={() => setShowJoinModal(false)}
                className="absolute top-4 right-4 w-10 h-10 rounded-full bg-tc-surface hover:bg-tc-surface-highlight flex items-center justify-center transition-colors"
                data-testid="join-modal-close"
              >
                <X className="w-5 h-5 text-tc-text-secondary" />
              </button>

              {/* Header */}
              <div className="mb-6">
                <div className="w-14 h-14 rounded-2xl bg-tc-primary/10 flex items-center justify-center mb-4">
                  <Zap className="w-7 h-7 text-tc-primary" />
                </div>
                <h3 className="text-2xl font-heading font-semibold text-white mb-2">
                  Gabung Program
                </h3>
                <p className="text-tc-primary font-medium text-lg">
                  {selectedCampaign.title}
                </p>
              </div>

              {/* Campaign Features */}
              <div className="mb-6 p-4 bg-tc-surface-highlight/50 rounded-xl">
                <p className="text-sm text-tc-text-secondary mb-3">Yang Anda dapatkan:</p>
                <ul className="space-y-2">
                  {selectedCampaign.features.slice(0, 4).map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-tc-secondary flex-shrink-0" />
                      <span className="text-tc-text-secondary">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Telegram Info */}
              <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <Send className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-400 font-medium mb-1">Akses Grup Telegram</p>
                    <p className="text-xs text-tc-text-secondary">
                      Setelah mendaftar, Anda akan mendapat kode verifikasi. Kirim kode tersebut ke bot Telegram untuk mendapat akses ke grup trading.
                    </p>
                  </div>
                </div>
              </div>

              {verificationCode ? (
                <div className="text-center space-y-4 py-6">
                  <CheckCircle className="w-12 h-12 text-green-400 mx-auto" />
                  <h3 className="text-xl font-bold text-white">Pendaftaran Berhasil!</h3>
                  <p className="text-gray-400">Kode verifikasi Anda:</p>
                  <div
                    className="bg-gray-800 rounded-lg p-4 font-mono text-2xl tracking-widest text-tc-secondary select-all cursor-pointer"
                    onClick={() => { navigator.clipboard.writeText(verificationCode); toast.success('Kode disalin!'); }}
                    data-testid="verification-code-display"
                  >
                    {verificationCode}
                  </div>
                  <div className="text-sm text-gray-400 space-y-1">
                    <p>Klik tombol di bawah atau DM <span className="text-white font-medium">@TCASIWBot</span></p>
                    <p>Kirim: <code className="bg-gray-800 px-2 py-0.5 rounded">/verify {verificationCode}</code></p>
                  </div>
                  <p className="text-xs text-gray-500">Kode berlaku 24 jam</p>
                  <Button
                    onClick={() => window.open(`https://t.me/TCASIWBot?start=verify_${verificationCode}`, '_blank', 'noopener,noreferrer')}
                    className="mt-4 bg-[#0088cc] hover:bg-[#0077b5] text-white font-bold rounded-xl"
                    data-testid="verification-telegram-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    Verifikasi di Telegram
                  </Button>
                  <button
                    onClick={handleResendCode}
                    disabled={isResending}
                    className="text-sm text-tc-text-secondary hover:text-white transition-colors disabled:opacity-40"
                    data-testid="resend-code-btn"
                  >
                    {isResending ? 'Mengirim ulang...' : 'Kode kedaluwarsa? Kirim ulang kode baru'}
                  </button>
                  <Button
                    variant="outline"
                    onClick={() => { setShowJoinModal(false); setVerificationCode(null); setJoinForm({ email: '', roboforex_account: '', agreed_terms: false }); }}
                    className="mt-2 border-tc-border text-tc-text-secondary hover:text-white rounded-xl"
                    data-testid="verification-close-btn"
                  >
                    Tutup
                  </Button>
                </div>
              ) : (
                <>
                  {/* Form */}
                  <form onSubmit={handleJoinSubmit} className="space-y-4">
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-tc-text-secondary" />
                      <Input
                        type="email"
                        placeholder="Email Anda *"
                        value={joinForm.email}
                        onChange={(e) => setJoinForm(prev => ({ ...prev, email: e.target.value }))}
                        className="bg-tc-surface border-tc-border text-white placeholder:text-tc-text-secondary h-14 rounded-xl pl-12"
                        required
                        data-testid="join-email-input"
                      />
                    </div>
                    <div className="relative">
                      <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-tc-text-secondary" />
                      <Input
                        type="text"
                        placeholder="Nomor akun RoboForex MT5 *"
                        value={joinForm.roboforex_account}
                        onChange={(e) => setJoinForm(prev => ({ ...prev, roboforex_account: e.target.value }))}
                        className="bg-tc-surface border-tc-border text-white placeholder:text-tc-text-secondary h-14 rounded-xl pl-12"
                        required
                        data-testid="join-roboforex-input"
                      />
                    </div>

                    {/* Terms and Conditions */}
                    <div className="flex items-start gap-3 p-4 bg-tc-surface-highlight/50 rounded-xl">
                      <Checkbox
                        id="terms"
                        checked={joinForm.agreed_terms}
                        onCheckedChange={(checked) => setJoinForm(prev => ({ ...prev, agreed_terms: checked }))}
                        className="mt-0.5 border-tc-border data-[state=checked]:bg-tc-primary data-[state=checked]:border-tc-primary"
                        data-testid="join-terms-checkbox"
                      />
                      <label htmlFor="terms" className="text-sm text-tc-text-secondary cursor-pointer">
                        Saya menyetujui Syarat dan Ketentuan dan memahami bahwa trading mengandung risiko. Konten bersifat edukasi, bukan nasihat keuangan. Saya mengonfirmasi bahwa akun RoboForex MT5 saya valid. *
                      </label>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-tc-primary hover:bg-tc-primary-hover text-white py-6 text-lg font-bold tracking-wide rounded-xl"
                      data-testid="join-submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Memproses...
                        </>
                      ) : (
                        <>
                          GABUNG SEKARANG - GRATIS
                          <ChevronRight className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>
                  </form>

                  <p className="text-xs text-tc-text-secondary text-center mt-4">
                    Dengan bergabung, Anda setuju menerima update program melalui email dan Telegram.
                  </p>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default CampaignsPage;
